import { createSlice } from '@reduxjs/toolkit';
import {
  getLatestAnalysisRecommendations,
  requestAnalysisRecommendations,
  getAnalysisRecommendationsStatus,
  createAnalysisOrder,
  getAnalysisResults,
} from './analysis-operations';
import {
  AnalysisOrderStatus,
  AnalysisState,
  PaymentType,
  RawAnalysisResponse,
  RawAnalysisSet,
} from './AnalysisTypes';

const emptySet = (): RawAnalysisSet => ({
  complex_id: 0,
  complex_name: null,
  set_name: '',
  price: 0,
  rcc_price: 0,
  analyses: [],
  payment_type: PaymentType.SELF_PAY,
  status: AnalysisOrderStatus.NEW,
});

/** Нормализация ответа: ставим дефолты, если бек не прислал поля */
function withDefaults(payload: RawAnalysisResponse): RawAnalysisResponse {
  const recommended = payload.recommended_analyses ?? emptySet();
  const vitamins = payload.vitamins ?? emptySet();

  return {
    ...payload,
    recommended_analyses: {
      ...recommended,
      payment_type: recommended.payment_type ?? PaymentType.SELF_PAY,
      status: recommended.status ?? AnalysisOrderStatus.NEW,
    },
    vitamins: {
      ...vitamins,
      payment_type: vitamins.payment_type ?? PaymentType.SELF_PAY,
      status: vitamins.status ?? AnalysisOrderStatus.NEW,
    },
  };
}

const isCompleted = (payload: RawAnalysisResponse) =>
  payload.status === 'completed' ||
  (!payload.status && Boolean(payload.recommended_analyses && payload.vitamins));

const initialState: AnalysisState = {
  data: null,
  loading: false,
  polling: false,

  orderLoading: false,
  orderError: null,

  error: null,

  results: null,
  resultsLoading: false,
  resultsError: null,
};

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    resetAnalysisState: () => initialState,
    invalidateAnalysisData: state => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: builder => {
    const applyPayload = (state: AnalysisState, payload: RawAnalysisResponse) => {
      if (isCompleted(payload)) {
        state.data = withDefaults(payload);
        state.polling = false;
        state.loading = false;
        return;
      }

      state.data = state.data ?? withDefaults(payload);
      state.polling = true;
      state.loading = false;
    };

    /* -------- GET latest recommendations -------- */
    builder.addCase(getLatestAnalysisRecommendations.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getLatestAnalysisRecommendations.fulfilled, (state, { payload }) => {
      applyPayload(state, payload);
    });
    builder.addCase(getLatestAnalysisRecommendations.rejected, (state, { payload, error }) => {
      if (payload?.status === 409) {
        state.loading = false;
        state.polling = true;
        state.error = null;
        return;
      }
      state.loading = false;
      state.polling = false;
      state.error = payload?.message || error.message || null;
    });

    /* -------- POST request recommendations -------- */
    builder.addCase(requestAnalysisRecommendations.pending, state => {
      state.polling = true;
      state.error = null;
    });
    builder.addCase(requestAnalysisRecommendations.fulfilled, (state, { payload }) => {
      applyPayload(state, payload);
    });
    builder.addCase(requestAnalysisRecommendations.rejected, (state, { payload, error }) => {
      state.polling = false;
      state.loading = false;
      state.error = payload?.message || error.message || null;
    });

    /* -------- GET recommendations status -------- */
    builder.addCase(getAnalysisRecommendationsStatus.fulfilled, (state, { payload }) => {
      applyPayload(state, payload);
    });
    builder.addCase(getAnalysisRecommendationsStatus.rejected, (state, { payload, error }) => {
      state.polling = false;
      state.loading = false;
      state.error = payload?.message || error.message || null;
    });

    /* -------- POST create order -------- */
    builder.addCase(createAnalysisOrder.pending, state => {
      state.orderLoading = true;
      state.orderError = null;
    });
    builder.addCase(createAnalysisOrder.fulfilled, state => {
      state.orderLoading = false;
    });
    builder.addCase(createAnalysisOrder.rejected, (state, { payload, error }) => {
      state.orderLoading = false;
      state.orderError = payload?.message || error.message || null;
    });

    /* -------- GET results -------- */
    builder.addCase(getAnalysisResults.pending, state => {
      state.resultsLoading = true;
      state.resultsError = null;
    });
    builder.addCase(getAnalysisResults.fulfilled, (state, { payload }) => {
      state.resultsLoading = false;
      state.results = payload;
    });
    builder.addCase(getAnalysisResults.rejected, (state, { payload, error }) => {
      state.resultsLoading = false;
      state.resultsError = payload?.message || error.message || null;
    });
  },
});

export const { resetAnalysisState, invalidateAnalysisData } = analysisSlice.actions;
export default analysisSlice.reducer;
